import { MediaPlayer, PlayerEvent } from '@mi-front/core';
import debugFn from 'debug';
import EventEmitter from 'events';

/**
 * Player that creates and manages HTML5 AudioElement instances for live URL-based audio playback.
 * For live streams, a new audio element is created on each play() call to avoid stale/buffered content.
 * The audio element is disposed when paused or when a new one is created.
 */
export class LiveUrlAudioPlayer extends EventEmitter implements MediaPlayer {
  private debug: debugFn.Debugger;
  private playbackId?: string;
  private currentAudioElement: HTMLAudioElement | null = null;
  private startTime: number | null = null;
  private timeupdateHandler?: () => void;
  private errorHandler?: (e: Event) => void;
  private endedHandler?: () => void;

  constructor(
    private src: string,
    private config: { playbackId?: string },
  ) {
    super();
    this.playbackId = config.playbackId;
    this.debug = debugFn(`LiveUrlAudioPlayer-${config.playbackId || 'default'}`);
  }

  /**
   * Creates a new audio element for live streaming.
   * Each call creates a fresh element to avoid stale/buffered content.
   */
  private createAudioElement(): HTMLAudioElement {
    const audioElement = document.createElement('audio');
    audioElement.preload = 'auto';
    audioElement.controls = false;
    audioElement.style.display = 'none';
    audioElement.crossOrigin = 'anonymous'; // Allow CORS for live streams
    audioElement.src = this.src;

    // Explicitly trigger loading to start buffering immediately
    audioElement.load();

    // Set up timeupdate listener to emit timestamp updates
    this.timeupdateHandler = () => {
      if (this.startTime === null) {
        // First timeupdate, set start time
        this.startTime = Date.now();
      }

      // For live streams, use current time relative to start
      // Convert audio currentTime (seconds) to milliseconds
      const currentTimeMs = audioElement.currentTime * 1000;
      const ts = this.startTime + currentTimeMs;

      this.emit(PlayerEvent.TS_UPDATE, ts);
    };
    audioElement.addEventListener('timeupdate', this.timeupdateHandler);

    this.errorHandler = (e: Event) => {
      const error = new Error(
        `Audio playback error: ${audioElement.error?.message || 'Unknown error'}`,
      );
      this.debug('audio error: %s', error.message);
      this.emit(PlayerEvent.ERROR, error);
    };
    audioElement.addEventListener('error', this.errorHandler);

    this.endedHandler = () => {
      this.emit(PlayerEvent.PLAYBACK_END);
    };
    audioElement.addEventListener('ended', this.endedHandler);

    // Append to body (hidden) - must be in DOM for some browsers to load
    document.body.appendChild(audioElement);

    return audioElement;
  }

  /**
   * Disposes the current audio element, removing event listeners and cleaning up resources.
   */
  private disposeCurrentAudio(): void {
    if (!this.currentAudioElement) {
      return;
    }

    const audio = this.currentAudioElement;

    // Remove all event listeners BEFORE clearing src to prevent error events
    if (this.timeupdateHandler) {
      audio.removeEventListener('timeupdate', this.timeupdateHandler);
      this.timeupdateHandler = undefined;
    }
    if (this.errorHandler) {
      audio.removeEventListener('error', this.errorHandler);
      this.errorHandler = undefined;
    }
    if (this.endedHandler) {
      audio.removeEventListener('ended', this.endedHandler);
      this.endedHandler = undefined;
    }

    this.currentAudioElement = null;
    this.startTime = null;

    // Pause and clear src, but don't call load() to avoid triggering error events
    // The element will be removed from DOM anyway, so load() is unnecessary
    audio.pause();
    audio.src = '';

    // Remove from DOM if it was added
    if (audio.parentNode) {
      audio.parentNode.removeChild(audio);
    }
  }

  dispose(): void {
    this.disposeCurrentAudio();
    this.removeAllListeners();
  }

  play(): void | Promise<void> {
    // Dispose previous audio element if it exists
    if (this.currentAudioElement) {
      this.debug('disposing previous audio element');
      this.disposeCurrentAudio();
    }

    // Emit SEEKING_START to indicate loading has begun
    // This will set isSeeking = true in the playback store
    this.emit(PlayerEvent.SEEKING_START);

    // Create new audio element for fresh live stream
    this.debug('creating new audio element for live stream');
    this.currentAudioElement = this.createAudioElement();

    const audio = this.currentAudioElement;

    // Set up listener for when playback actually starts
    // This will emit SEEKING_END to indicate loading is complete
    // and PLAYING to indicate playback has started (which sets isPlaying=true in the store)
    const handlePlaying = () => {
      this.debug('playback started, emitting SEEKING_END and PLAYING');
      this.emit(PlayerEvent.SEEKING_END);
      this.emit(PlayerEvent.PLAYING);
    };

    const handleError = () => {
      // On error, still emit SEEKING_END to clear the seeking state
      this.debug('playback error, emitting SEEKING_END');
      this.emit(PlayerEvent.SEEKING_END);
    };

    audio.addEventListener('playing', handlePlaying, { once: true });
    audio.addEventListener('error', handleError, { once: true });

    this.debug('play');
    const playPromise = audio.play();

    // If play() returns a promise, handle potential rejection
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.catch((err) => {
        this.debug('play() promise rejected: %s', err);
        // Emit SEEKING_END to clear the seeking state if play() fails
        this.emit(PlayerEvent.SEEKING_END);
        audio.removeEventListener('playing', handlePlaying);
        audio.removeEventListener('error', handleError);
      });
    }

    return playPromise;
  }

  pause(): void {
    if (!this.currentAudioElement || this.currentAudioElement.paused) {
      return;
    }

    this.debug('pause');

    this.currentAudioElement.pause();
    // Dispose audio element when paused to ensure fresh start on next play
    this.disposeCurrentAudio();
  }

  seek(args: { time: number; ts: number }): void | Promise<void> {
    // Seeking is not supported for live streams
    this.debug('seek attempted on live stream, ignoring');
    return;
  }

  mute(): void {
    if (this.currentAudioElement) {
      this.currentAudioElement.muted = true;
    }
  }

  unmute(): void {
    if (this.currentAudioElement) {
      this.currentAudioElement.muted = false;
    }
  }

  isMuted(): boolean {
    if (!this.currentAudioElement) {
      return false;
    }
    return this.currentAudioElement.muted;
  }
}
